/**
 * goauth's identifier rules, mirrored so the form can answer before a round
 * trip. The server stays the authority — this only spares the user a request
 * that is certain to fail.
 *
 * The pattern is deliberately narrow upstream: forbidding `@` is what lets
 * goauth decide by itself whether an identifier is a username or an email, and
 * restricting to ASCII keeps a Cyrillic "аdmin" from rendering as the real one.
 */
const USERNAME_PATTERN = /^[a-z0-9_]{3,32}$/

/** Names whose holder could impersonate the service. Rejected by goauth outright. */
const RESERVED = new Set([
  'admin',
  'administrator',
  'root',
  'system',
  'support',
  'help',
  'api',
  'auth',
  'goauth',
  'security',
  'noreply',
  'no-reply',
  'postmaster',
  'webmaster',
  'abuse',
])

export type UsernameProblem = 'pattern' | 'reserved'

/** goauth lowercases before validating, so `Alice` is accepted and stored as `alice`. */
export const normalizeUsername = (raw: string): string => raw.trim().toLowerCase()

export function checkUsername(raw: string): UsernameProblem | null {
  const value = normalizeUsername(raw)

  if (!USERNAME_PATTERN.test(value)) return 'pattern'
  if (RESERVED.has(value)) return 'reserved'

  return null
}
